import { registerMutation, useMutation } from '../../utils/DataFetchingClient';
import { storeTimeAndReturnData } from '../../utils/ajaxUtils';
import { getTrackExamPageUrl } from '../../utils/RoutingUtils';
import { useFullTrack } from '../tracks';
import { BASE_URI_LC, BASE_URI } from '../../constants/ApiConstants';
import { http } from '../http';
import getLang from 'I18n/utils/getLang';
import { useCallback } from 'react';
import { addQuestionSubmissionCache, setMostRecentExamAttemptCache, useTrackAwardProgress, useTrackProgressById } from '../userProgress';
import { MS_IN_A_MINUTE, MS_IN_A_DAY, TRACK_AWARD_EXPIRY_DAYS } from '../../constants/TimeConstants';
const EXAMS_API_URL = `${BASE_URI}/exams`;
const EXAM_ATTEMPTS_API_URL = `${BASE_URI_LC}/exam-attempts`;
const START_EXAM = registerMutation({
  fieldName: 'startExam',
  args: ['examId', 'language'],

  fetcher({
    examId,
    language = getLang()
  }) {
    return http.postWithResponse(`${EXAM_ATTEMPTS_API_URL}/${examId}/start/${language}`).then(storeTimeAndReturnData);
  }

});
const RESUME_EXAM = registerMutation({
  fieldName: 'resumeExam',
  args: ['attemptId'],

  fetcher({
    attemptId
  }) {
    return http.postWithResponse(`${EXAMS_API_URL}/${attemptId}/resume`).then(storeTimeAndReturnData);
  }

});
const SUBMIT_EXAM_QUESTION = registerMutation({
  fieldName: 'submitExamQuestion',
  args: ['attemptId', 'questionId', 'answerIds'],

  fetcher({
    attemptId,
    questionId,
    answerIds
  }) {
    return http.post(`${EXAMS_API_URL}/${attemptId}/answer`, {
      data: {
        questionId,
        answerIds
      }
    });
  }

});
const VALIDATE_EXAM = registerMutation({
  fieldName: 'validateExam',
  args: ['attemptId'],

  fetcher({
    attemptId
  }) {
    return http.postWithResponse(`${EXAMS_API_URL}/${attemptId}/validate`).then(storeTimeAndReturnData);
  }

});
const SEND_EMAIL_BREAKDOWN = registerMutation({
  fieldName: 'sendEmailBreakdown',
  args: ['attemptId', 'trackId', 'language'],

  fetcher({
    attemptId,
    trackId,
    language = getLang()
  }) {
    return http.post(`${EXAMS_API_URL}/breakdown`, {
      data: {
        language,
        attemptId,
        trackId,
        examPath: getTrackExamPageUrl(trackId.toString()).slice(1)
      }
    });
  }

});
export const useExam = trackId => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId);
  return {
    exam: track ? track.exam : null,
    examLoading: trackLoading,
    examError: trackError
  };
};
export const useStartExam = () => {
  const [mutate, {
    loading: startExamLoading,
    data: startExamData,
    error: startExamError,
    called: startExamCalled
  }] = useMutation(START_EXAM);
  const startExam = useCallback((trackId, examId, language = getLang()) => {
    return mutate({
      variables: {
        examId,
        language
      },

      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.startExam);
        }
      }

    }).then(result => result.data && result.data.startExam);
  }, [mutate]);
  return [startExam, {
    startExamData,
    startExamLoading,
    startExamError,
    startExamCalled
  }];
};
export const useResumeExam = () => {
  const [mutate, {
    loading: resumeExamLoading,
    data: resumeExamData,
    error: resumeExamError,
    called: resumeExamCalled
  }] = useMutation(RESUME_EXAM);
  const resumeExam = useCallback((trackId, attemptId) => {
    return mutate({
      variables: {
        attemptId
      },

      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.resumeExam);
        }
      }

    }).then(result => result.data && result.data.resumeExam);
  }, [mutate]);
  return [resumeExam, {
    resumeExamData,
    resumeExamLoading,
    resumeExamError,
    resumeExamCalled
  }];
};
export const useSubmitExamQuestion = () => {
  const [mutate, {
    loading: submitExamQuestionLoading,
    data: submitExamQuestionData,
    error: submitExamQuestionError,
    called: submitExamQuestionCalled
  }] = useMutation(SUBMIT_EXAM_QUESTION);
  const submitExamQuestion = useCallback((trackId, attemptId, questionId, answerIds) => {
    return mutate({
      variables: {
        attemptId,
        questionId,
        answerIds
      },

      update() {
        addQuestionSubmissionCache(trackId, attemptId, questionId, answerIds);
      }

    }).then(result => result.data);
  }, [mutate]);
  return [submitExamQuestion, {
    submitExamQuestionData,
    submitExamQuestionLoading,
    submitExamQuestionError,
    submitExamQuestionCalled
  }];
};
export const useValidateExam = () => {
  const [mutate, {
    loading: validateExamLoading,
    data: validateExamData,
    error: validateExamError,
    called: validateExamCalled
  }] = useMutation(VALIDATE_EXAM);
  const validateExam = useCallback((trackId, attemptId) => {
    return mutate({
      variables: {
        attemptId
      },

      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.validateExam);
        }
      }

    }).then(result => result.data && result.data.validateExam);
  }, [mutate]);
  return [validateExam, {
    validateExamData,
    validateExamLoading,
    validateExamError,
    validateExamCalled
  }];
};
export const useSendEmailBreakdown = () => {
  const [mutate, {
    loading: sendEmailBreakdownLoading,
    data: sendEmailBreakdownData,
    error: sendEmailBreakdownError,
    called: sendEmailBreakdownCalled
  }] = useMutation(SEND_EMAIL_BREAKDOWN);
  const sendEmailBreakdown = useCallback((attemptId, trackId, language = getLang()) => {
    return mutate({
      variables: {
        attemptId,
        trackId,
        language
      }
    }).then(result => result.data);
  }, [mutate]);
  return [sendEmailBreakdown, {
    sendEmailBreakdownData,
    sendEmailBreakdownLoading,
    sendEmailBreakdownError,
    sendEmailBreakdownCalled
  }];
};
export const useExamExpiry = trackId => {
  const {
    award,
    allProgressLoading,
    allProgressError
  } = useTrackAwardProgress(Number(trackId));
  return {
    isExpired: award && award.isExpired,
    expiresOn: award && award.expiresAt,
    examExpiryLoading: allProgressLoading,
    examExpiryError: allProgressError
  };
};
export const useExamAttempts = trackId => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  return {
    examAttempts: trackProgressById ? trackProgressById.examAttempts : undefined,
    trackProgressByIdLoading,
    trackProgressByIdError
  };
};
export const useMostRecentExamAttempt = trackId => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId);
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  const mostRecentExamAttempt = track && track.exam && trackProgressById && trackProgressById.examAttempts[trackProgressById.examIdToRecentAttemptId[track.exam.id]];
  return {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading: trackLoading || trackProgressByIdLoading,
    mostRecentExamAttemptError: trackError || trackProgressByIdError
  };
};
export const useAttemptExpiry = trackId => {
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId);
  const attemptExpiresOn = mostRecentExamAttempt && mostRecentExamAttempt.attemptFinishTime ? mostRecentExamAttempt.attemptFinishTime + TRACK_AWARD_EXPIRY_DAYS * MS_IN_A_DAY : null;
  const isAttemptExpired = attemptExpiresOn ? new Date().getTime() > attemptExpiresOn : false;
  return {
    attemptExpiresOn,
    isAttemptExpired,
    attemptExpiryLoading: mostRecentExamAttemptLoading,
    attemptExpiryError: mostRecentExamAttemptError
  };
};
export const useIsExamAttemptInProgress = trackId => {
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId);
  return {
    isExamAttemptInProgress: mostRecentExamAttempt ? !mostRecentExamAttempt.attemptFinishTime : false,
    isExamAttemptInProgressLoading: mostRecentExamAttemptLoading,
    isExamAttemptInProgressError: mostRecentExamAttemptError
  };
};
export const useExamDeadline = trackId => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId);
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId);
  const examDeadline = track && track.exam && mostRecentExamAttempt && mostRecentExamAttempt.attemptStartTime + track.exam.durationMinutes * MS_IN_A_MINUTE;
  return {
    examDeadline,
    examDeadlineLoading: trackLoading || mostRecentExamAttemptLoading,
    examDeadlineError: trackError || mostRecentExamAttemptError
  };
};